import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import {
  CsatSurveyMethod,
  SurveySendTimeOptions,
} from "@redotech/redo-support-settings-common/csat-survey";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

export const SendCsatSurveyOnConversationClosedConfigSchema = Schema.Struct({
  surveySendTime: Schema.optional(Schema.Enums(SurveySendTimeOptions)),
  surveyMethods: Schema.optional(Schema.Array(Schema.Enums(CsatSurveyMethod))),
  allowRemarks: Schema.optional(Schema.Boolean),
  headerImage: Schema.optional(Schema.String),
  surveyMessage: Schema.optional(Schema.String),
  /** Only survey tickets received at these inboxes; empty means every inbox. */
  allowedInboxEmails: Schema.optional(Schema.Array(Schema.String)),
  abTestEnabled: Schema.optional(Schema.Boolean),
  abTestVariants: Schema.optional(
    Schema.Array(
      Schema.Struct({
        id: Schema.String,
        name: Schema.String,
        weight: Schema.Number,
        surveySendTime: Schema.optional(Schema.Enums(SurveySendTimeOptions)),
        emailTemplateId: Schema.optional(Schema.String),
      }),
    ),
  ),
});

export type SendCsatSurveyOnConversationClosedConfig =
  typeof SendCsatSurveyOnConversationClosedConfigSchema.Type;

const bind = makeBind<SendCsatSurveyOnConversationClosedConfig>();

/**
 * Default rule: Send CSAT survey on conversation closed (support-v2)
 *
 * When a conversation transitions to CLOSED, schedules a CSAT survey email
 * via a Temporal workflow that sleeps for the configured delay before sending.
 * Replaces the legacy cron-based CSAT flow for support-v2 teams.
 *
 * Defaults to `enabled: false` — enabled per-team via migration script for
 * teams that had `csat.enabled = true`.
 */
export const sendCsatSurveyOnConversationClosedRule = {
  id: "default-send-csat-survey-on-conversation-closed",
  name: "Send CSAT survey on conversation closed",
  description:
    "When a conversation is closed, send a CSAT survey email after the configured delay.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_STATUS_CHANGED,

  configSchema: SendCsatSurveyOnConversationClosedConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-send-csat-survey-on-conversation-closed",
    enabled: false,
    sortIndex: 6,
    configurations: {
      surveySendTime: SurveySendTimeOptions.ONE_HOUR,
      surveyMethods: [CsatSurveyMethod.EMAIL],
      allowRemarks: true,
      headerImage: "",
      surveyMessage: "",
      allowedInboxEmails: [],
    },
  },

  template: {
    name: "Send CSAT survey on conversation closed",
    description:
      "When a conversation is closed, send a CSAT survey email after the configured delay.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["CLOSED"],
              queryOperator: QueryOperator.IS,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.SEND_CSAT_SURVEY,
            surveySendTime: bind(
              "surveySendTime",
              SurveySendTimeOptions.ONE_HOUR,
            ),
            surveyMethods: bind("surveyMethods", [CsatSurveyMethod.EMAIL]),
            allowRemarks: bind("allowRemarks", true),
            headerImage: bind("headerImage", ""),
            surveyMessage: bind("surveyMessage", ""),
            allowedInboxEmails: bind("allowedInboxEmails", []),
            abTestEnabled: bind("abTestEnabled", false),
            abTestVariants: bind("abTestVariants", []),
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<
  string,
  SendCsatSurveyOnConversationClosedConfig
>;
